import { Subject, Subscription } from 'rxjs';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import {
  IAppState,
  ISharedData,
} from 'src/app/shared/state-management/state-interfaces';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { HelperService } from 'src/app/shared/utils/helper.service';
import {
  convertNumber,
  convertParseDateTime,
  extractDateTimeFromDateString,
  Utility,
} from 'src/app/shared/utils/utility.service';
import { PageName } from 'src/app/shared/utils/user-roles';
import { NgRedux } from '@angular-redux/store';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  ICriteria,
  IFilter,
  IUser,
  SharedUser,
} from 'src/app/user-management/user-management-interfaces';
import { UserManagementService } from 'src/app/user-management/user-management.service';
import { UserGroupComponent } from 'src/app/user-management/user-group/user-group.component';
import { MultiUserShareAccessComponent } from 'src/app/user-management/multi-user-share-access/multi-user-share-access.component';
import { DashboardManagementService } from 'src/app/dashboard/dashboard-management.service';
import { FilterUsersComponent } from 'src/app/user-management/filter-users/filter-users.component';
import { StateManagementService } from 'src/app/shared/services/state-management.service';
import { LocalizationService } from 'src/app/shared/services/localization.service';

@Component({
  selector: 'app-shared-user-list',
  templateUrl: './shared-user-list.component.html',
  styleUrls: ['./shared-user-list.component.scss'],
})
export class SharedUserListComponent implements OnInit, OnDestroy {
  shareedDataSubscription?: Subscription;
  readonly LANG_CONST = LANG_CONST;
  CONSTANTS = CONSTANTS;
  userList$: SharedUser | undefined;
  currentAlphabet: string | undefined;
  sharedData?: ISharedData;
  siteId!: string;
  userType: string | undefined;
  userGroupId: string | undefined;
  PageName = PageName;
  searchId = 'search-user';
  searchKeyPress: boolean = false;
  userListsubscription: Subscription | undefined;
  subscription: Subscription | undefined;
  profileImageSrc: string = 'assets/images/portrait-happy.jpg';
  private readonly searchSubject$ = new Subject<string | undefined>();
  rolesSelected: string[] | undefined = [];
  filter: IFilter[] = [];
  filterCount: number = 0;
  userManagementSubscription: Subscription | undefined;
  isNestedUserCall: boolean = false;
  totalCount: number = 0;
  isDashboard: boolean = true;
  commonAccessControl!: boolean;
  searchedWord: string = '';
  convertNumber = convertNumber;
  constructor(
    private userManagementService: UserManagementService,
    private sharedDataService: SharedDataService,
    private helperService: HelperService,
    private ngRedux: NgRedux<IAppState>,
    private router: Router,
    private route: ActivatedRoute,
    public dashboardManagementService: DashboardManagementService,
    private stateManagementService: StateManagementService,
    private localizationService: LocalizationService
  ) {
    this.ngRedux.select('dashboardUsersList').subscribe((userList: any) => {
      if (JSON.stringify(this.userList$) !== JSON.stringify(userList)) {
        this.userList$ = userList;
        if (!this.searchedWord && !this.filter.length) {
          this.totalCount = this.userList$?.totalCount || 0;
        }
      }
    });
  }

  ngOnInit(): void {
    this.localizationService.setupLocalization();
    this.userList$ = undefined;
    this.commonAccessControl =
      this.stateManagementService.getSharedData()?.selectedSite?.commonAccessControl;
    this.subscription = this.route.firstChild!.params.subscribe((params) => {
      this.userType = Utility.decodeFromBase64(params['groupTypeName']);
      this.userGroupId = Utility.decodeFromBase64(params['userGroupId']);
    });

    this.shareedDataSubscription = this.sharedDataService.currentData.subscribe(
      (sharedData) => {
        this.sharedData = sharedData;
        const currentSiteId = this.sharedData?.selectedSite?.siteId;
        if (currentSiteId && currentSiteId !== this.siteId) {
          this.siteId = String(currentSiteId);
          const criteria: ICriteria = {
            size: 10000,
            searchText: '',
            userGroupId: this.userGroupId,
          };
          this.callGetUsersAPI(criteria);
        }
      }
    );

    // server-side search //
    const criteria: ICriteria = {
      size: 10000,
      searchText: '',
      userGroupId: this.userGroupId,
    };
    this.searchSubject$
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe((searchQuery: any) => {
        this.searchedWord = searchQuery;
        criteria.searchText = searchQuery;
        this.userManagementService
          .getDashboardUsers(criteria, true, this.filter)
          .subscribe({
            next: () => {
              this.searchKeyPress = false;
            },
            error: (err: Error) => {
              this.searchKeyPress = false;
              if (window.__dev__) console.log('getUsersBySite: ', err);
            },
          });
      });
    /**
     * This service is resposible when record updating, we previously using this service in floor-management list rendering also in get update record case
     */
    this.userManagementSubscription =
      this.userManagementService?.triggerRerender?.subscribe(
        (result: boolean) => {
          if (result) {
            this.searchKeyPress = true;
            this.callGetUsersAPI(criteria);
            this.userManagementService.setTriggerRerender(false);
          }
        }
      );
  }
  /**
   *
   * @param time get time format
   * @returns return formated time
   */
  getContentFormatDate(
    time: string,
    currentFormat: 't' | 'HH:mm',
    format: 'hh:mma'
  ) {
    return convertParseDateTime(time, currentFormat, format);
  }

  checkIfNew(name: string) {
    if (this.currentAlphabet === name?.charAt(0).toLowerCase()) return false;
    this.currentAlphabet = name?.charAt(0).toLowerCase();
    return true;
  }

  /**
   * @description: add new user
   * @inut {none}
   * @output {void}
   */
  addUser(): void {
    const dialogInstance = this.helperService.openModalDialog(
      MultiUserShareAccessComponent,
      {
        group: { userGroupId: this.userGroupId },
        users: [],
        isDashboard: this.isDashboard,
      },
      CONSTANTS.FIXED_SCREEN_MODAL
    );
    dialogInstance?.afterClosed().subscribe((users: any) => {
      const criteria: ICriteria = {
        size: 10000,
        searchText: '',
        userGroupId: this.userGroupId,
      };

      this.userListsubscription = this.userManagementService
        .getDashboardUsers(criteria)
        .subscribe();
    });
  }

  /**
   * @description: add new user group
   * @inut {none}
   * @output {void}
   */
  addUserGroup(): void {
    const dialogInstance = this.helperService.openModalDialog(
      UserGroupComponent,
      {
        isEditable: false,
        enableBackOperation: false,
        defaultNavigation: CONSTANTS.BUILDING,
      },
      CONSTANTS.FIXED_SCREEN_MODAL
    );
  }

  encode(data: string): string {
    return Utility.encodeToBase64(data);
  }

  searchThis(data: string) {
    this.searchKeyPress = true;
    this.searchSubject$.next(data);
  }

  trackByUsers(index: number, user: IUser) {
    return user;
  }

  /**
   * @description: ##selectFilter function opens the modal popup for Filter Users.
   * @input none
   * @output { void }
   */

  selectFilter() {
    const dialogRef = this.helperService.openModalDialog(
      FilterUsersComponent,
      this.rolesSelected,
      [CONSTANTS.FIXED_SCREEN_MODAL]
    );

    dialogRef?.afterClosed().subscribe((filterUsers: string[]) => {
      if (filterUsers) {
        this.rolesSelected = filterUsers;
        const mappingTypes = filterUsers?.map((userRole: any) => {
          return userRole === 5 || userRole === 6 ? 7 : userRole;
        });
        const updateSelectedRoles = [...new Set(mappingTypes)];

        this.filter = updateSelectedRoles.map((filterCriteria: any) => {
          return { field: 'userType', value: filterCriteria };
        });
        this.searchKeyPress = true;
        const criteria: ICriteria = {
          size: 10000,
          searchText: this.searchedWord,
          userGroupId: this.userGroupId,
        };
        this.callGetUsersAPI(criteria, this.filter);
      } else return;
      this.filterCount = this.rolesSelected?.length;
    });
  }

  /**
   * @description: #callGetUserAPI function will call an API for getDashboardUsers
   * @inut {none}
   * @output {void}
   */
  callGetUsersAPI(criteria: ICriteria, filter?: IFilter[]): void {
    this.isNestedUserCall = true;
    this.userListsubscription = this.userManagementService
      .getDashboardUsers(criteria, true, filter)
      .subscribe({
        next: () => {
          this.searchKeyPress = false;
          this.isNestedUserCall = false;
        },
        error: () => {
          this.searchKeyPress = false;
          this.isNestedUserCall = false;
        },
      });
  }

  /**
   *
   * @param time get date format
   * @returns return date format
   */
  getDate(date: string) {
    const response = convertParseDateTime(
      extractDateTimeFromDateString(String(date)),
      "yyyy-MM-dd'T'HH:mm:ss",
      'yyyy/MM/dd hh:mm a'
    );
    return response;
  }

  ngOnDestroy(): void {
    this.shareedDataSubscription?.unsubscribe();
    this.userListsubscription?.unsubscribe();
    this.userManagementSubscription?.unsubscribe();
    this.totalCount = 0;
    this.stateManagementService.updateDashboardUsersList(undefined);
  }
}
